import { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Crown, Trophy, Medal, Award, Flame, Timer } from "lucide-react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

function useCountdown(deadlineIso) {
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        const t = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(t);
    }, []);
    if (!deadlineIso) return null;
    const target = new Date(deadlineIso).getTime();
    const ms = Math.max(0, target - now);
    const d = Math.floor(ms / 86400000);
    const h = Math.floor((ms % 86400000) / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    const s = Math.floor((ms % 60000) / 1000);
    return { d, h, m, s, total: ms };
}

function PodiumRow({ leaders, pointsKey = "points", testIdPrefix = "", requirePoints = false }) {
    // Iter 30.49 — Podium ne montre que des personnes qui ont *réellement* scoré
    // si requirePoints=true (challenge hebdo). Les slots vides affichent un
    // placeholder "À conquérir" plutôt que de mettre les 0 pt sur le podium.
    const scoring = requirePoints
        ? leaders.filter((l) => (l[pointsKey] || 0) > 0)
        : leaders;
    const top3 = scoring.slice(0, 3);
    // - requirePoints + au moins 1 recruteur en attente : on rend quand même 3 slots
    //   "À conquérir" pour donner envie de scorer.
    // - requirePoints + aucun recruteur du tout : on cache (le parent affiche un état vide)
    // - !requirePoints (onglet "Toujours") : ancien comportement, on cache si pas de top3
    if (top3.length === 0) {
        if (!(requirePoints && leaders.length > 0)) return null;
    }
    // Toujours rendre 3 slots visuels (1er, 2ème, 3ème) même si vides
    return (
        <div className="grid grid-cols-3 gap-3 lg:gap-6 mb-8" data-testid={`${testIdPrefix}podium`}>
            {[1, 0, 2].map((idx) => {
                const l = top3[idx];
                const heights = ["h-32", "h-44", "h-28"];
                const colors = [
                    { bg: "#FCDB53", icon: Medal, label: "2" },
                    { bg: "#EE722E", icon: Crown, label: "1" },
                    { bg: "#F89F63", icon: Award, label: "3" },
                ];
                const c = colors[[1, 0, 2].indexOf(idx)];
                const Icon = c.icon;

                // Slot vide → placeholder dim "À conquérir"
                if (!l) {
                    return (
                        <motion.div
                            key={`empty-${idx}`}
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: idx * 0.1 }}
                            className="flex flex-col items-center opacity-40"
                            data-testid={`${testIdPrefix}podium-empty-${c.label}`}
                        >
                            <div
                                className="w-16 h-16 lg:w-20 lg:h-20 rounded-2xl flex items-center justify-center font-extrabold text-white/40 text-2xl mb-3 border-2 border-dashed border-white/20 bg-white/3"
                            >
                                ?
                            </div>
                            <p className="text-sm lg:text-base font-extrabold text-white/40 text-center mb-1 truncate max-w-full px-2">
                                À conquérir
                            </p>
                            <p className="text-xs text-white/30 font-bold mb-3">— pts</p>
                            <div
                                className={`${heights[[1, 0, 2].indexOf(idx)]} w-full rounded-t-3xl flex flex-col items-center justify-start pt-4 relative overflow-hidden border-2 border-dashed border-white/10`}
                                style={{ background: "rgba(255,255,255,0.03)" }}
                            >
                                <Icon className="w-7 h-7 mb-1 text-white/20" />
                                <span className="text-3xl font-extrabold text-white/20">
                                    {c.label}
                                </span>
                            </div>
                        </motion.div>
                    );
                }

                return (
                    <motion.div
                        key={l.user_id}
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.1 }}
                        className="flex flex-col items-center"
                    >
                        {l.avatar ? (
                            <img
                                src={l.avatar}
                                alt={l.name}
                                className="w-16 h-16 lg:w-20 lg:h-20 rounded-2xl object-cover ring-4 mb-3"
                                style={{ boxShadow: `0 0 0 3px ${c.bg}` }}
                            />
                        ) : (
                            <div
                                className="w-16 h-16 lg:w-20 lg:h-20 rounded-2xl flex items-center justify-center font-extrabold text-shariti-black text-2xl mb-3"
                                style={{ background: c.bg }}
                            >
                                {l.name?.[0]}
                            </div>
                        )}
                        <p className="text-sm lg:text-base font-extrabold text-white text-center mb-1 truncate max-w-full px-2">
                            {l.name}
                        </p>
                        <p className="text-xs text-shariti-orange-light font-bold mb-3">
                            {l[pointsKey]} pts
                        </p>
                        <div
                            className={`${heights[[1, 0, 2].indexOf(idx)]} w-full rounded-t-3xl flex flex-col items-center justify-start pt-4 relative overflow-hidden`}
                            style={{
                                background: `linear-gradient(180deg, ${c.bg} 0%, ${c.bg}aa 100%)`,
                            }}
                        >
                            <Icon
                                className="w-7 h-7 mb-1"
                                color="#181818"
                                fill="#181818"
                            />
                            <span className="text-3xl font-extrabold text-shariti-black">
                                {c.label}
                            </span>
                        </div>
                    </motion.div>
                );
            })}
        </div>
    );
}

function RankList({ leaders, pointsKey = "points", currentUid, startRank = 4, unranked = false }) {
    return (
        <div className="space-y-2" data-testid="leaderboard-list">
            {leaders.map((l, i) => {
                const rank = startRank + i;
                const isMe = l.user_id === currentUid;
                const showDash = unranked || (l[pointsKey] || 0) === 0;
                return (
                    <motion.div
                        key={l.user_id}
                        initial={{ opacity: 0, x: -16 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.04 }}
                        className={`flex items-center gap-4 p-4 rounded-3xl glass-card transition-all ${
                            isMe ? "border-shariti-orange/60 bg-shariti-orange/5" : ""
                        }`}
                        data-testid={`leader-row-${l.user_id}`}
                    >
                        <span
                            className="w-10 h-10 rounded-xl bg-white/3 flex items-center justify-center font-extrabold text-white/60 shrink-0"
                            data-testid={`leader-rank-${l.user_id}`}
                        >
                            {showDash ? "—" : rank}
                        </span>
                        {l.avatar ? (
                            <img
                                src={l.avatar}
                                alt={l.name}
                                className="w-12 h-12 rounded-2xl object-cover"
                            />
                        ) : (
                            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-shariti-orange to-shariti-yellow flex items-center justify-center font-bold text-shariti-black">
                                {l.name?.[0]}
                            </div>
                        )}
                        <div className="flex-1 min-w-0">
                            <p className="font-extrabold text-white truncate flex items-center gap-2">
                                {l.name}
                                {isMe && (
                                    <span className="text-[10px] bg-shariti-orange text-white px-2 py-0.5 rounded-full uppercase tracking-widest">
                                        Toi
                                    </span>
                                )}
                            </p>
                            <p className="text-xs text-white/50 capitalize">
                                {l.role}
                                {l.completed_modules != null && ` · ${l.completed_modules} modules`}
                                {l.badges_count != null && ` · ${l.badges_count} badges`}
                            </p>
                        </div>
                        <div className="text-right">
                            <p className="text-2xl font-extrabold text-shariti-orange leading-none">
                                {l[pointsKey]}
                            </p>
                            <p className="text-[10px] font-thin tracking-widest text-white/40 uppercase">
                                points
                            </p>
                        </div>
                    </motion.div>
                );
            })}
        </div>
    );
}

export default function Leaderboard() {
    const { user } = useAuth();
    const [tab, setTab] = useState("weekly");
    const [allTime, setAllTime] = useState([]);
    const [weekly, setWeekly] = useState(null);

    useEffect(() => {
        api.get("/leaderboard").then((r) => setAllTime(r.data));
        api.get("/leaderboard/weekly").then((r) => setWeekly(r.data));
    }, []);

    const countdown = useCountdown(weekly?.deadline);
    const weeklyLeaders = weekly?.leaders || [];

    return (
        <Layout
            title="Leaderboard"
            subtitle="Classement des recruteurs"
        >
            {/* Tabs */}
            <div
                className="flex gap-2 mb-6 p-1.5 glass-card rounded-full w-fit"
                data-testid="leaderboard-tabs"
            >
                <button
                    onClick={() => setTab("weekly")}
                    data-testid="tab-weekly"
                    className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm font-bold transition-all ${
                        tab === "weekly"
                            ? "bg-shariti-orange text-white"
                            : "text-white/55 hover:text-white"
                    }`}
                >
                    <Flame className="w-4 h-4" />
                    Cette semaine
                </button>
                <button
                    onClick={() => setTab("all-time")}
                    data-testid="tab-all-time"
                    className={`px-5 py-2 rounded-full text-sm font-bold transition-all ${
                        tab === "all-time"
                            ? "bg-shariti-orange text-white"
                            : "text-white/55 hover:text-white"
                    }`}
                >
                    Toujours
                </button>
            </div>

            <AnimatePresence mode="wait">
                {tab === "weekly" && (
                    <motion.div
                        key="weekly"
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                    >
                        {/* Challenge banner */}
                        <div
                            className="rounded-3xl p-6 lg:p-7 mb-6 relative overflow-hidden bg-gradient-to-br from-shariti-orange to-shariti-orange-light text-white"
                            data-testid="weekly-challenge-banner"
                        >
                            <div className="absolute -bottom-10 -right-10 w-48 h-48 rounded-full bg-shariti-yellow/30 blur-3xl" />
                            <div className="relative z-10 grid lg:grid-cols-3 gap-4 items-center">
                                <div>
                                    <div className="flex items-center gap-2 mb-2">
                                        <Crown className="w-5 h-5" />
                                        <p className="text-xs font-thin tracking-[0.3em] uppercase opacity-90">
                                            Challenge de la semaine
                                        </p>
                                    </div>
                                    <h2 className="text-2xl lg:text-3xl font-extrabold tracking-tight leading-tight mb-1">
                                        Le top 1 décroche le badge
                                    </h2>
                                    <p className="text-sm font-light opacity-90">
                                        + 200 pts bonus & badge "Champion semaine"
                                    </p>
                                </div>

                                {countdown && (
                                    <div
                                        className="bg-shariti-black/30 backdrop-blur rounded-2xl p-4"
                                        data-testid="weekly-countdown"
                                    >
                                        <div className="flex items-center gap-2 mb-2">
                                            <Timer className="w-4 h-4" />
                                            <p className="text-xs font-thin tracking-[0.3em] uppercase opacity-90">
                                                Fin du défi dans
                                            </p>
                                        </div>
                                        <div className="flex items-baseline gap-2">
                                            <span className="text-3xl lg:text-4xl font-extrabold leading-none tabular-nums">
                                                {countdown.d}j
                                            </span>
                                            <span className="text-2xl font-extrabold tabular-nums">
                                                {String(countdown.h).padStart(2, "0")}h
                                            </span>
                                            <span className="text-xl font-bold tabular-nums opacity-80">
                                                {String(countdown.m).padStart(2, "0")}
                                            </span>
                                        </div>
                                    </div>
                                )}

                                {weekly?.previous_winner && (
                                    <div
                                        className="bg-shariti-black/30 backdrop-blur rounded-2xl p-4 flex items-center gap-3"
                                        data-testid="previous-winner-card"
                                    >
                                        {weekly.previous_winner.avatar ? (
                                            <img
                                                src={weekly.previous_winner.avatar}
                                                alt={weekly.previous_winner.name}
                                                className="w-12 h-12 rounded-2xl object-cover"
                                            />
                                        ) : (
                                            <div className="w-12 h-12 rounded-2xl bg-shariti-yellow flex items-center justify-center font-extrabold text-shariti-black">
                                                {weekly.previous_winner.name?.[0]}
                                            </div>
                                        )}
                                        <div className="min-w-0">
                                            <p className="text-xs font-thin uppercase tracking-widest opacity-80">
                                                Champion·ne précédent·e
                                            </p>
                                            <p className="font-extrabold truncate">
                                                {weekly.previous_winner.name}
                                            </p>
                                            <p className="text-xs font-bold opacity-90">
                                                {weekly.previous_winner.points} pts
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Weekly podium — n'affiche que les utilisateurs ayant scoré */}
                        <PodiumRow
                            leaders={weeklyLeaders}
                            pointsKey="weekly_points"
                            testIdPrefix="weekly-"
                            requirePoints
                        />
                        {(() => {
                            const scoring = weeklyLeaders.filter(
                                (l) => (l.weekly_points || 0) > 0,
                            );
                            const zero = weeklyLeaders.filter(
                                (l) => (l.weekly_points || 0) === 0,
                            );
                            const restScoring = scoring.slice(3);
                            return (
                                <>
                                    {restScoring.length > 0 && (
                                        <RankList
                                            leaders={restScoring}
                                            pointsKey="weekly_points"
                                            currentUid={user?.user_id}
                                            startRank={4}
                                        />
                                    )}
                                    {zero.length > 0 && (
                                        <>
                                            {(scoring.length > 0 || restScoring.length > 0) && (
                                                <p
                                                    className="text-[10px] font-thin tracking-[0.25em] uppercase text-white/40 mt-6 mb-3 px-1"
                                                    data-testid="weekly-zero-section-label"
                                                >
                                                    En attente d’un premier point cette semaine
                                                </p>
                                            )}
                                            <RankList
                                                leaders={zero}
                                                pointsKey="weekly_points"
                                                currentUid={user?.user_id}
                                                unranked
                                            />
                                        </>
                                    )}
                                </>
                            );
                        })()}
                        {weeklyLeaders.length === 0 && (
                            <div className="glass-card rounded-3xl p-10 text-center">
                                <p className="text-white/50">
                                    Le challenge démarre — sois le premier à scorer cette semaine !
                                </p>
                            </div>
                        )}
                    </motion.div>
                )}

                {tab === "all-time" && (
                    <motion.div
                        key="all-time"
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                    >
                        <PodiumRow leaders={allTime} pointsKey="points" requirePoints />
                        {(() => {
                            const scoring = allTime.filter((l) => (l.points || 0) > 0);
                            const zero = allTime.filter((l) => (l.points || 0) === 0);
                            const restScoring = scoring.slice(3);
                            return (
                                <>
                                    {restScoring.length > 0 && (
                                        <RankList
                                            leaders={restScoring}
                                            pointsKey="points"
                                            currentUid={user?.user_id}
                                            startRank={4}
                                        />
                                    )}
                                    {zero.length > 0 && (
                                        <>
                                            {scoring.length > 0 && (
                                                <p
                                                    className="text-[10px] font-thin tracking-[0.25em] uppercase text-white/40 mt-6 mb-3 px-1"
                                                    data-testid="alltime-zero-section-label"
                                                >
                                                    En attente d’un premier point
                                                </p>
                                            )}
                                            <RankList
                                                leaders={zero}
                                                pointsKey="points"
                                                currentUid={user?.user_id}
                                                unranked
                                            />
                                        </>
                                    )}
                                </>
                            );
                        })()}
                    </motion.div>
                )}
            </AnimatePresence>
        </Layout>
    );
}
